import { useTriggerLinkedInScrape } from '../../hooks/admin/useAdminMutations'
import { useAdminUser } from '../../hooks/admin/useAdminQueries'
import { useAdminStore } from '../../stores/adminStore'
import { AdminTriggerLinkedInScrapeDto } from '../../types/admin-types'
import React, { useState } from 'react'

const UserDetail: React.FC = () => {
    const selectedUserId = useAdminStore((state) => state.selectedUserId)
    const openEditUserModal = useAdminStore((state) => state.openEditUserModal)

    const { data: userData, isLoading, isError, error } = useAdminUser(selectedUserId)
    const { mutate: triggerScrape, isPending: isScraping } = useTriggerLinkedInScrape()

    const [linkedInUrl, setLinkedInUrl] = useState('')

    const handleTriggerScrape = () => {
        if (!selectedUserId || !linkedInUrl) {
            alert('Please enter a valid LinkedIn URL.') // Use better feedback
            return
        }
        const data: AdminTriggerLinkedInScrapeDto = { linkedinUrl: linkedInUrl }
        triggerScrape(
            { id: selectedUserId, data },
            {
                onSuccess: (result) => {
                    alert(result.message) // Use toast
                    setLinkedInUrl('')
                },
                onError: (err) => {
                    alert(
                        `Error triggering scrape: ${
                            err instanceof Error ? err.message : 'Unknown error'
                        }`,
                    ) // Use toast
                },
            },
        )
    }

    if (isLoading) {
        return <div className="p-4 text-center text-gray-500">Loading user details...</div>
    }

    if (isError) {
        return (
            <div className="p-4 text-center text-red-500">
                Error loading user details: {error?.message || 'Unknown error'}
            </div>
        )
    }

    if (!userData) {
        return (
            <div className="p-8 flex justify-center items-center h-full">
                <p className="text-gray-500">Select a user to see details.</p>
            </div>
        )
    }

    return (
        <div className="p-6 space-y-6 h-full overflow-y-auto flex flex-col">
            {/* Header with Actions */}
            <div className="flex justify-between items-center border-b pb-4 mb-6 flex-shrink-0">
                <div>
                    <h2 className="text-xl font-semibold">
                        {userData.first_name} {userData.last_name}
                    </h2>
                    <p className="text-sm text-gray-500">{userData.email}</p>
                    {userData.title && <p className="text-sm text-gray-500">{userData.title}</p>}
                </div>
                <div className="space-x-2 flex items-center">
                    <button
                        onClick={() => openEditUserModal(userData.id)} // Pass user ID
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700"
                    >
                        Edit User
                    </button>
                </div>
            </div>

            {/* User Information Section */}
            <div className="flex-grow overflow-y-auto pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Basic Info */}
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-lg font-medium mb-3 border-b pb-2">
                            Basic Information
                        </h3>
                        <dl className="space-y-2 text-sm">
                            <div className="grid grid-cols-3 gap-2">
                                <dt className="font-medium text-gray-500">Username</dt>
                                <dd className="col-span-2 text-gray-900">{userData.username}</dd>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <dt className="font-medium text-gray-500">Company</dt>
                                <dd className="col-span-2 text-gray-900">
                                    {userData.user_org_name}
                                </dd>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <dt className="font-medium text-gray-500">Licensing Org</dt>
                                <dd className="col-span-2 text-gray-900">
                                    {userData.org?.name || 'N/A'}
                                </dd>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <dt className="font-medium text-gray-500">Timezone</dt>
                                <dd className="col-span-2 text-gray-900">
                                    {userData.default_timezone}
                                </dd>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <dt className="font-medium text-gray-500">Alternate Names</dt>
                                <dd className="col-span-2 text-gray-900">
                                    {userData.alternate_names || 'N/A'}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    {/* Relevant Topics */}
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-lg font-medium mb-3 border-b pb-2">Relevant Topics</h3>
                        <div className="space-y-3 text-sm">
                            <div>
                                <h4 className="font-medium text-gray-600 mb-1">Bio</h4>
                                <p className="text-gray-800 whitespace-pre-wrap">
                                    {userData.userRelevantTopics?.bio || 'Not set'}
                                </p>
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-600 mb-1">Tone of Voice</h4>
                                <p className="text-gray-800 whitespace-pre-wrap">
                                    {userData.userRelevantTopics?.tone_of_voice || 'Not set'}
                                </p>
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-600 mb-1">Target Audience</h4>
                                {userData.userRelevantTopics?.target_audience_structured &&
                                userData.userRelevantTopics.target_audience_structured.length >
                                    0 ? (
                                    <ul className="list-disc list-inside space-y-1 pl-2">
                                        {userData.userRelevantTopics.target_audience_structured.map(
                                            (audience, index) => (
                                                <li key={index}>
                                                    <span className="font-semibold">
                                                        {audience.audience_name}:
                                                    </span>
                                                    <span className="text-gray-700 ml-1">
                                                        {audience.description}
                                                    </span>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                ) : (
                                    <p className="text-gray-500 italic">
                                        No structured audience set.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* LinkedIn Scrape Section */}
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-medium mb-3">Trigger LinkedIn Scrape</h3>
                    <div className="flex space-x-2">
                        <input
                            type="url"
                            placeholder="Enter LinkedIn Profile URL"
                            value={linkedInUrl}
                            onChange={(e) => setLinkedInUrl(e.target.value)}
                            className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            disabled={isScraping}
                        />
                        <button
                            onClick={handleTriggerScrape}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isScraping || !linkedInUrl}
                        >
                            {isScraping ? 'Scraping...' : 'Trigger Scrape'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserDetail
